import { Box, Text } from '@youversion/design-system-components';
import React, { type JSX } from 'react';

interface Props extends React.HTMLProps<HTMLDivElement> {
  className?: string;
  id?: string;
  isChecked?: boolean;
  label: string;
  onChange: () => void;
  secondaryLabel?: string;
  secondaryTitle?: string;
  startIcon?: JSX.Element;
  title?: string;
  value?: string | number;
}

/**
 * Radio List Item.
 *
 * @param className - Class name(s) to apply.
 * @param id - The value to set for the `id` property.
 * @param isChecked - Boolean to tell what radio is checked.
 * @param label - Text used for the label.
 * @param onChange - The callback handler for the click event.
 * @param secondaryLabel - Label used for the secondary action.
 * @param secondaryTitle - Title for the secondary label.
 * @param startIcon - Icon that goes on the start.
 * @param title - Tells the title of the item. I.e. Frequency / Process Date etc.
 * @param props - Customization options passed to container.
 * @returns React.ReactNode.
 *
 * @example
 *
 * ```typescriptreact
 * <RadioListItem
 *  Title="YouVersion"
 *  isChecked={true}
 *  secondaryLabel="Process Gift On"
 * />
 * ```
 */
export default function RadioListItem({
  className = '',
  isChecked,
  id,
  label,
  onChange,
  secondaryLabel,
  secondaryTitle,
  startIcon,
  title,
  value,
}: Props) {
  const inputClasses = `
  w-[20px] h-[20px] mie-0.5 flex-none cursor-pointer  appearance-none
bg-white outline checked:outline-offset-[-2px] checked:ring-offset-[-2px] outline-black outline-1 focus-visible:outline-0 rounded-full
checked:bg-blue-20 checked:outline-blue-30 checked:outline-2 checked:ring-2 checked:ring-blue-30`;

  return (
    <div
      className={`${className} flex justify-between items-center cursor-pointer ${
        isChecked ? 'bg-blue-10 dark:bg-blue-50' : 'bg-canvas-light dark:bg-canvas-secondary-dark'
      } p-3 rounded-1 gap-2`}
      id={id}
      onClick={onChange}
    >
      <Box className='flex flex-col items-start justify-center'>
        <Box className='flex items-center space-x-1'>
          {startIcon ? <Box className='m-0'>{startIcon}</Box> : null}
          {title ? (
            <Text className='mie-2' size='md' variant='label'>
              {title}
            </Text>
          ) : null}
          <Text className='mis-1' size='lg' variant='label'>
            {label}
          </Text>
        </Box>
        <Box className='flex items-center space-x-1'>
          {secondaryTitle ? (
            <div className='mis-1'>
              <span className='text-13 dark:text-text-dark text-text-light font-bold'>{secondaryTitle}</span>
            </div>
          ) : null}
          {secondaryLabel ? (
            <div className='max-w-sm'>
              <span className='text-13 dark:text-text-dark text-text-light'>{secondaryLabel}</span>
            </div>
          ) : null}
        </Box>
      </Box>
      <div className='relative inline-block'>
        <input checked={isChecked} className={inputClasses} onChange={onChange} type='radio' value={value} />
      </div>
    </div>
  );
}
